// Admin: reminder sweep run log — last sweep time, throughput per run, next scheduled sweep.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, PlayCircle, Bell } from "lucide-react";
import { useState } from "react";
import {
  adminListReminderRuns,
  adminRunReminderSweepNow,
} from "@/lib/api/invoice-reminders.functions";

export const Route = createFileRoute(
  "/_authenticated/portal/admin/invoice-reminder-runs",
)({
  head: () => ({ meta: [{ title: "Admin · Reminder run log" }] }),
  component: ReminderRunsPage,
});

function fmt(iso: string | null | undefined) {
  return iso ? new Date(iso).toLocaleString("en-ZA") : "—";
}

function durationMs(a?: string | null, b?: string | null) {
  if (!a || !b) return null;
  const d = new Date(b).getTime() - new Date(a).getTime();
  return d >= 0 ? d : null;
}

function ReminderRunsPage() {
  const list = useServerFn(adminListReminderRuns);
  const runNow = useServerFn(adminRunReminderSweepNow);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin-reminder-runs"],
    queryFn: () => list(),
    refetchInterval: 30_000,
  });
  const [running, setRunning] = useState(false);

  async function trigger() {
    setRunning(true);
    try { await runNow(); await refetch(); } finally { setRunning(false); }
  }

  const runs = data?.runs ?? [];
  const lastRun = runs[0];
  const nextRunAt = data?.next_run_at ?? null;

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl text-navy flex items-center gap-2">
            <Bell className="h-5 w-5" /> Reminder sweep runs
          </h1>
          <p className="text-sm text-muted-foreground">
            Every daily and manual invoice-reminder sweep, with throughput and errors per run.
          </p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/portal/admin/invoice-reminders"
            className="inline-flex items-center rounded-md border border-border px-3 py-2 text-xs hover:bg-secondary"
          >
            Reminder settings
          </Link>
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            <RefreshCw className="mr-1 h-3 w-3" /> Refresh
          </Button>
          <Button size="sm" onClick={trigger} disabled={running}>
            {running ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <PlayCircle className="mr-1 h-3 w-3" />}
            Run sweep now
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Card>
          <CardContent className="py-4">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Last sweep</p>
            <p className="font-display text-lg text-navy">{fmt(lastRun?.started_at)}</p>
            <p className="text-xs text-muted-foreground">
              {lastRun ? `${lastRun.sent} sent · ${lastRun.skipped} skipped · ${lastRun.scanned} scanned` : "No runs yet"}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Next sweep</p>
            <p className="font-display text-lg text-navy">{fmt(nextRunAt)}</p>
            <p className="text-xs text-muted-foreground">Scheduled via daily cron</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Runs on file</p>
            <p className="font-display text-lg text-navy">{runs.length}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Run history</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground"><Loader2 className="mr-1 inline h-4 w-4 animate-spin" /> Loading…</p>
          ) : runs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No sweeps recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="py-2 pr-3">Started</th>
                    <th className="py-2 pr-3">Duration</th>
                    <th className="py-2 pr-3">Trigger</th>
                    <th className="py-2 pr-3">Target</th>
                    <th className="py-2 pr-3">Scanned</th>
                    <th className="py-2 pr-3">Sent</th>
                    <th className="py-2 pr-3">Skipped</th>
                    <th className="py-2 pr-3">Errors</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {runs.map((r: any) => {
                    const d = durationMs(r.started_at, r.finished_at);
                    const errors = Array.isArray(r.errors) ? r.errors : [];
                    return (
                      <tr key={r.id}>
                        <td className="py-2 pr-3 font-mono text-xs">{fmt(r.started_at)}</td>
                        <td className="py-2 pr-3">{d === null ? (r.finished_at ? "—" : "…in progress") : `${(d / 1000).toFixed(1)}s`}</td>
                        <td className="py-2 pr-3"><Badge variant="outline">{r.triggered_by}</Badge></td>
                        <td className="py-2 pr-3 font-mono text-xs">{r.target_reference ?? "—"}</td>
                        <td className="py-2 pr-3">{r.scanned}</td>
                        <td className="py-2 pr-3 text-emerald-700">{r.sent}</td>
                        <td className="py-2 pr-3">{r.skipped}</td>
                        <td className="py-2 pr-3 text-rose-700 max-w-[280px]">
                          {errors.length === 0 ? "—" : (
                            <details><summary className="cursor-pointer text-xs">{errors.length} error(s)</summary>
                              <ul className="mt-1 space-y-0.5 text-[11px]">
                                {errors.slice(0, 20).map((e: any, i: number) => <li key={i}>• {String(e)}</li>)}
                              </ul>
                            </details>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
